// Localized storefront display.
import { tr, useStoreLanguage } from '@/i18n';

import { useState, useEffect } from 'react';
import { Link } from '@/i18n/router';
import { COOKIE_SETTINGS_EVENT, getCookiePreferences, saveCookiePreferences } from '@/utils/analyticsConsent';

const CookieConsent = () => {
  useStoreLanguage();
  const [visible, setVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState(getCookiePreferences() || {
    necessary: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const consent = getCookiePreferences();
    const openSettings = () => {
      setPreferences(getCookiePreferences() || { necessary: true, analytics: false, marketing: false });
      setShowPreferences(true);
      setVisible(true);
    };
    window.addEventListener(COOKIE_SETTINGS_EVENT, openSettings);
    let timer: ReturnType<typeof setTimeout> | undefined;
    if (!consent) {
      timer = setTimeout(() => setVisible(true), 300);
    }
    return () => {
      clearTimeout(timer);
      window.removeEventListener(COOKIE_SETTINGS_EVENT, openSettings);
    };
  }, []);

  const acceptAll = () => {
    saveCookiePreferences({ necessary: true, analytics: true, marketing: true });
    setVisible(false);
  };

  const rejectAll = () => {
    saveCookiePreferences({ necessary: true, analytics: false, marketing: false });
    setVisible(false);
  };

  const savePreferences = () => {
    saveCookiePreferences({ ...preferences, necessary: true });
    setVisible(false);
    setShowPreferences(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-end justify-center pointer-events-none">
      <div
        role="dialog"
        aria-label={tr("Preferencias de cookies")}
        className="relative w-full max-w-2xl mx-4 mb-6 bg-white rounded-2xl border border-gray-100 pointer-events-auto animate-slideUp"
      >
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center space-x-3 mb-3">
            <div className="w-9 h-9 flex items-center justify-center bg-forest-50 rounded-full">
              <i className="ri-cookie-line text-xl text-forest-600"></i>
            </div>
            <h3 className="font-playfair text-lg font-semibold text-gray-900">{tr("Utilizamos cookies")}</h3>
          </div>

          {/* Description */}
          <p className="font-inter text-sm text-gray-600 leading-relaxed mb-5">{tr("Usamos cookies para mejorar tu experiencia de navegación, personalizar contenido y analizar nuestro tráfico. Puedes aceptar todas, rechazarlas o personalizar tus preferencias.")}{tr(' ')}
            <Link
              to="/cookies"
              className="text-forest-600 hover:text-forest-700 underline underline-offset-2 cursor-pointer"
            >{tr("Más información")}</Link>
          </p>

          {/* Preferences Panel */}
          {tr(showPreferences && (
            <div className="mb-5 space-y-3 border border-gray-100 rounded-xl p-4 bg-gray-50/50">
              {/* Necessary */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-inter text-sm font-medium text-gray-900">{tr("Necesarias")}</p>
                  <p className="font-inter text-xs text-gray-500">{tr("Esenciales para el funcionamiento del sitio")}</p>
                </div>
                <div className="w-10 h-6 flex items-center justify-center">
                  <div className="w-10 h-5 bg-forest-500 rounded-full relative cursor-not-allowed opacity-70">
                    <div className="absolute right-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow" />
                  </div>
                </div>
              </div>

              {/* Analytics */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-inter text-sm font-medium text-gray-900">{tr("Analíticas")}</p>
                  <p className="font-inter text-xs text-gray-500">{tr("Nos ayudan a entender cómo usas el sitio")}</p>
                </div>
                <button
                  onClick={() => setPreferences(p => ({ ...p, analytics: !p.analytics }))}
                  className={`w-10 h-5 rounded-full relative transition-colors cursor-pointer ${
                    preferences.analytics ? 'bg-forest-500' : 'bg-gray-300'
                  }`}
                >
                  <div
                    className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                      preferences.analytics ? 'right-0.5' : 'left-0.5'
                    }`}
                  />
                </button>
              </div>

              {/* Marketing */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-inter text-sm font-medium text-gray-900">{tr("Marketing")}</p>
                  <p className="font-inter text-xs text-gray-500">{tr("Permiten mostrarte contenido personalizado")}</p>
                </div>
                <button
                  onClick={() => setPreferences(p => ({ ...p, marketing: !p.marketing }))}
                  className={`w-10 h-5 rounded-full relative transition-colors cursor-pointer ${
                    preferences.marketing ? 'bg-forest-500' : 'bg-gray-300'
                  }`}
                >
                  <div
                    className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                      preferences.marketing ? 'right-0.5' : 'left-0.5'
                    }`}
                  />
                </button>
              </div>
            </div>
          ))}

          {/* Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            {tr(showPreferences ? (
              <>
                <button
                  onClick={savePreferences}
                  className="flex-1 px-5 py-2.5 bg-forest-600 hover:bg-forest-700 text-white text-sm font-inter font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >{tr("Guardar preferencias")}</button>
                <button
                  onClick={() => setShowPreferences(false)}
                  className="flex-1 px-5 py-2.5 border border-gray-200 hover:bg-gray-50 text-gray-700 text-sm font-inter font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >{tr("Volver")}</button>
              </>
            ) : (
              <>
                <button
                  onClick={acceptAll}
                  className="flex-1 px-5 py-2.5 bg-forest-600 hover:bg-forest-700 text-white text-sm font-inter font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >{tr("Aceptar todas")}</button>
                <button
                  onClick={rejectAll}
                  className="flex-1 px-5 py-2.5 border border-gray-200 hover:bg-gray-50 text-gray-700 text-sm font-inter font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >{tr("Rechazar")}</button>
                <button
                  onClick={() => setShowPreferences(true)}
                  className="flex-1 px-5 py-2.5 border border-gray-200 hover:bg-gray-50 text-gray-700 text-sm font-inter font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >{tr("Personalizar")}</button>
              </>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
